import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, Loader2, Send, Download, Camera, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import EquipoSelect from "@/components/EquipoSelect";

import { logoColor as LOGO_URL } from "@/lib/logos";
const MOTIVOS_BAJA = ["Obsolescencia tecnológica", "Daño irreparable", "Fin de vida útil",
  "Costo de reparación excede el valor del equipo", "Falla recurrente", "Siniestro", "Otro"];
const DESTINOS = ["Reciclaje", "Desecho / disposición final", "Devolución al cliente",
  "Almacenamiento en bodega", "Otro"];
const SECCIONES = [
  ["Estado del equipo", "estado_equipo", "Estado físico y operativo del equipo al momento de la baja..."],
  ["Descripción y justificación de la baja *", "descripcion", "Por qué se da de baja el equipo..."],
  ["Evaluación técnica *", "evaluacion_tecnica", "Análisis técnico que sustenta la baja..."],
  ["Recomendaciones", "recomendaciones", "Recomendaciones (reposición, renovación, etc.)..."],
  ["Observaciones", "observaciones", "Observaciones adicionales..."],
];

export default function BajaEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user, isAdmin } = useAuth();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [reportId, setReportId] = useState(id || null);
  const [folio, setFolio] = useState("");
  const [estado, setEstado] = useState("borrador");
  const [readOnly, setReadOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [clients, setClients] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const lastSnapshotRef = useRef("");
  const stateRef = useRef(null);
  const reportIdRef = useRef(reportId);
  const inFlightSaveRef = useRef(null);
  useEffect(() => { reportIdRef.current = reportId; }, [reportId]);

  const [baja, setBaja] = useState({
    cliente_id: "", cliente_nombre: "", cliente_rut: "", contacto_nombre: "", contacto_email: "",
    sucursal: "", direccion: "", tipo_equipo: "", marca: "", modelo: "", numero_serie: "",
    numero_box: "", codigo_interno: "", fecha_baja: new Date().toISOString().split("T")[0],
    motivo_baja: "", anos_uso: "", estado_equipo: "", descripcion: "", evaluacion_tecnica: "",
    recomendaciones: "", destino_final: "", observaciones: "", imagenes: [], tecnico_id: "",
  });

  const ub = (f, v) => setBaja((p) => ({ ...p, [f]: v }));

  useEffect(() => {
    api.get("/api/clients").then((r) => setClients(r.data)).catch(() => {});
    if (isAdmin) api.get("/api/auth/technicians").then((r) => setTecnicos(r.data)).catch(() => {});
  }, [api, isAdmin]);

  useEffect(() => {
    if (!id) return;
    api.get(`/api/bajas/${id}`).then((r) => {
      const d = r.data;
      const loaded = {
        cliente_id: d.cliente_id || "", cliente_nombre: d.cliente_nombre || "",
        cliente_rut: d.cliente_rut || "", contacto_nombre: d.contacto_nombre || "",
        contacto_email: d.contacto_email || "", sucursal: d.sucursal || "", direccion: d.direccion || "",
        tipo_equipo: d.tipo_equipo || "", marca: d.marca || "", modelo: d.modelo || "",
        numero_serie: d.numero_serie || "", numero_box: d.numero_box || "",
        codigo_interno: d.codigo_interno || "", fecha_baja: d.fecha_baja || "",
        motivo_baja: d.motivo_baja || "", anos_uso: d.anos_uso || "", estado_equipo: d.estado_equipo || "",
        descripcion: d.descripcion || "", evaluacion_tecnica: d.evaluacion_tecnica || "",
        recomendaciones: d.recomendaciones || "", destino_final: d.destino_final || "",
        observaciones: d.observaciones || "", imagenes: d.imagenes || [], tecnico_id: d.tecnico_id || "",
      };
      setBaja(loaded);
      lastSnapshotRef.current = JSON.stringify(loaded);
      setFolio(d.folio); setReportId(d.id); setEstado(d.estado || "borrador");
      if (d.estado === "completado" && user?.cargo !== "admin") setReadOnly(true);
    }).catch(() => toast.error("Error al cargar el informe de baja"));
  }, [id, api, user?.cargo]);

  const selectClient = (clientId) => {
    const c = clients.find((x) => x.id === clientId);
    if (!c) return;
    setBaja((p) => ({
      ...p, cliente_id: clientId, cliente_nombre: c.nombre, cliente_rut: c.rut || "",
      contacto_nombre: c.contacto_nombre || "", contacto_email: c.contacto_email || "",
      sucursal: "", direccion: "",
    }));
  };
  const selectedClient = clients.find((c) => c.id === baja.cliente_id);

  const save = async ({ silent = false } = {}) => {
    if (readOnly) return reportId;
    if (!baja.cliente_nombre) { if (!silent) toast.error("Selecciona un cliente"); return null; }
    if (inFlightSaveRef.current) {
      try { await inFlightSaveRef.current; } catch (_) {}
    }
    const p = (async () => {
      setSaving(true);
      try {
        if (reportId) {
          await api.put(`/api/bajas/${reportId}`, baja);
          if (!silent) toast.success("Informe guardado");
          setLastSavedAt(Date.now());
          lastSnapshotRef.current = JSON.stringify(baja);
          return reportId;
        }
        const r = await api.post("/api/bajas", baja);
        setReportId(r.data.id); setFolio(r.data.folio);
        if (!silent) toast.success(`Informe creado: ${r.data.folio}`);
        setLastSavedAt(Date.now());
        lastSnapshotRef.current = JSON.stringify(baja);
        return r.data.id;
      } catch (e) {
        if (!silent) toast.error(e.response?.data?.detail || "Error al guardar");
        return null;
      } finally { setSaving(false); }
    })();
    inFlightSaveRef.current = p;
    try { return await p; }
    finally { if (inFlightSaveRef.current === p) inFlightSaveRef.current = null; }
  };

  // Auto-guardado del borrador (mismo patrón que MP).
  useEffect(() => { stateRef.current = baja; }, [baja]);
  useEffect(() => {
    if (readOnly || saving || sending) return;
    if (!baja.cliente_nombre) return;
    const snap = JSON.stringify(baja);
    if (snap === lastSnapshotRef.current) return;
    const t = setTimeout(() => { save({ silent: true }); }, 2000);
    return () => clearTimeout(t);
  }, [baja, readOnly, saving, sending]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush al cerrar la pestaña.
  useEffect(() => {
    const onBeforeUnload = () => {
      const rid = reportIdRef.current;
      const s = stateRef.current;
      if (!rid || !s || !s.cliente_nombre) return;
      const snap = JSON.stringify(s);
      if (snap === lastSnapshotRef.current) return;
      const token = localStorage.getItem("odf_token");
      const url = `${api.defaults.baseURL || ""}/api/bajas/${rid}`;
      try {
        fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json",
                     ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(s),
          keepalive: true,
        }).catch(() => {});
      } catch (_) { /* best effort */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [api]);

  const requiredMissing = () => {
    const m = [];
    if (!baja.cliente_nombre) m.push("Cliente");
    if (!baja.marca) m.push("Marca del equipo");
    if (!baja.modelo) m.push("Modelo del equipo");
    if (!baja.motivo_baja) m.push("Motivo de la baja");
    if (!baja.descripcion) m.push("Descripción y justificación");
    if (!baja.evaluacion_tecnica) m.push("Evaluación técnica");
    return m;
  };

  const send = async () => {
    if (readOnly) return;
    const missing = requiredMissing();
    if (missing.length > 0) { toast.error(`Faltan campos: ${missing.join(", ")}`); return; }
    setSending(true);
    try {
      const savedId = await save({ silent: true });
      if (!savedId) throw new Error("No se pudo guardar");
      const r = await api.post(`/api/bajas/${savedId}/complete`);
      toast.success(r.data.reenvio ? `Informe ${r.data.folio} reenviado` : `Informe ${r.data.folio} completado`);
      if (r.data.correo_enviado) toast.success("Correo enviado al cliente");
      else if (r.data.email_error) toast.error(`Correo no enviado: ${r.data.email_error}`);
      navigate("/tecnico/bajas");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al enviar");
    } finally { setSending(false); }
  };

  const downloadPdf = async () => {
    const savedId = reportId || (await save({ silent: true }));
    if (!savedId) return;
    try {
      const r = await api.get(`/api/bajas/${savedId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${folio || "informe-baja"}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar PDF"); }
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Solo se permiten imágenes"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Máximo 10MB por imagen"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/api/maintenance/images/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      ub("imagenes", [...baja.imagenes, r.data.id]);
      toast.success("Imagen subida");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir imagen"); }
  };

  const deleteImg = async (imgId) => {
    try {
      await api.delete(`/api/maintenance/images/${imgId}`);
      ub("imagenes", baja.imagenes.filter((x) => x !== imgId));
      toast.success("Imagen eliminada");
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
      <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />

      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-7" />
            <span className="text-sm font-semibold text-[#384091]">{folio || "Nuevo Informe de Baja"}</span>
            {estado === "completado" && <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">Completado</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <span className="hidden sm:inline-block text-[11px] text-gray-500 mr-1">
                {saving
                  ? "Guardando…"
                  : lastSavedAt
                    ? `Guardado ${new Date(lastSavedAt).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}`
                    : "Auto-guardado activo"}
              </span>
            )}
            {!readOnly && (
              <Button variant="outline" size="sm" onClick={() => save()} disabled={saving || sending}>
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                Guardar
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/bajas")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {readOnly && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 font-medium">
            🔒 Informe completado — Solo lectura. Un administrador puede editarlo.
          </div>
        )}

        {/* Cliente */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold text-lg text-[#384091]">Datos del cliente</h2>
            <div>
              <Label>Cliente *</Label>
              <Select value={baja.cliente_id} onValueChange={selectClient} disabled={readOnly}>
                <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
                <SelectContent className="bg-white">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {selectedClient?.direcciones?.length > 0 && (
              <div>
                <Label>Sucursal</Label>
                <Select value={baja.sucursal} onValueChange={(v) => {
                  const d = selectedClient.direcciones.find((x) => x.nombre === v);
                  ub("sucursal", v);
                  if (d) ub("direccion", `${d.direccion}, ${d.comuna}`);
                }} disabled={readOnly}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Seleccionar (opcional)..." /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {selectedClient.direcciones.map((d) => (
                      <SelectItem key={d.nombre} value={d.nombre}>{d.nombre} — {d.direccion}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Contacto</Label>
                <Input value={baja.contacto_nombre} onChange={(e) => ub("contacto_nombre", e.target.value)} autoComplete="name" className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>Correo</Label>
                <Input type="email" inputMode="email" autoComplete="email" value={baja.contacto_email} onChange={(e) => ub("contacto_email", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
            </div>
            {isAdmin && (
              <div>
                <Label>Técnico responsable</Label>
                <Select value={baja.tecnico_id} onValueChange={(v) => ub("tecnico_id", v)} disabled={readOnly}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Asignar técnico (por defecto: tú)..." /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {tecnicos.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Equipo */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-3">
            <h2 className="font-bold text-lg text-[#384091]">Equipo dado de baja</h2>
            {!readOnly && isAdmin && baja.cliente_id && (
              <EquipoSelect
                clienteId={baja.cliente_id}
                sucursal={baja.sucursal}
                onSelect={(e) => setBaja((p) => ({
                  ...p,
                  tipo_equipo: e.tipo_equipo || p.tipo_equipo,
                  marca: e.marca || p.marca,
                  modelo: e.modelo || p.modelo,
                  numero_serie: e.numero_serie || p.numero_serie,
                  numero_box: e.numero_box || p.numero_box,
                  codigo_interno: e.codigo_interno || p.codigo_interno,
                }))}
              />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Tipo de equipo</Label><Input value={baja.tipo_equipo} onChange={(e) => ub("tipo_equipo", e.target.value)} placeholder="Ej: Unidad dental, compresor..." className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>Marca *</Label><Input value={baja.marca} onChange={(e) => ub("marca", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>Modelo *</Label><Input value={baja.modelo} onChange={(e) => ub("modelo", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>N° Serie</Label><Input value={baja.numero_serie} onChange={(e) => ub("numero_serie", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>N° Box</Label><Input value={baja.numero_box} onChange={(e) => ub("numero_box", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>Código interno</Label><Input value={baja.codigo_interno} onChange={(e) => ub("codigo_interno", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
              <div><Label>Años de uso</Label><Input inputMode="numeric" value={baja.anos_uso} onChange={(e) => ub("anos_uso", e.target.value)} placeholder="Ej: 12" className="mt-1 bg-white" disabled={readOnly} /></div>
            </div>
          </CardContent>
        </Card>

        {/* Baja */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold text-lg text-[#384091]">Detalle de la baja</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label>Motivo de la baja *</Label>
                <Select value={baja.motivo_baja} onValueChange={(v) => ub("motivo_baja", v)} disabled={readOnly}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {MOTIVOS_BAJA.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Fecha de baja</Label>
                <Input type="date" value={baja.fecha_baja} onChange={(e) => ub("fecha_baja", e.target.value)} className="mt-1 bg-white" disabled={readOnly} /></div>
            </div>
            <div>
              <Label>Destino final del equipo</Label>
              <Select value={baja.destino_final} onValueChange={(v) => ub("destino_final", v)} disabled={readOnly}>
                <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent className="bg-white">
                  {DESTINOS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {SECCIONES.map(([label, key, ph]) => (
              <div key={key}>
                <Label>{label}</Label>
                <Textarea rows={4} value={baja[key]} onChange={(e) => ub(key, e.target.value)}
                  placeholder={ph} className="mt-1 bg-white" disabled={readOnly} />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Registro fotográfico */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-lg text-[#384091]">Registro fotográfico</h2>
              <Badge className="bg-blue-50 text-blue-700 border-0">{baja.imagenes.length} foto(s)</Badge>
            </div>
            <p className="text-xs text-gray-500">Puedes agregar todas las imágenes que necesites. Se optimizan automáticamente para el PDF.</p>
            {baja.imagenes.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {baja.imagenes.map((imgId, idx) => (
                  <div key={imgId} className="relative aspect-square rounded-lg border overflow-hidden">
                    <img src={`${api.defaults.baseURL}/api/maintenance/images/${imgId}`}
                      alt={`Imagen ${idx + 1}`} className="w-full h-full object-cover" />
                    {!readOnly && (
                      <button onClick={() => deleteImg(imgId)}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {!readOnly && (
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => cameraRef.current?.click()}
                  className="py-3 rounded-lg border-2 border-dashed border-[#0FB4B7]/40 flex items-center justify-center gap-2 text-[#0FB4B7] hover:bg-[#0FB4B7]/5">
                  <Camera className="h-4 w-4" /><span className="text-sm font-medium">Tomar foto</span>
                </button>
                <button onClick={() => galleryRef.current?.click()}
                  className="py-3 rounded-lg border-2 border-dashed border-[#384091]/40 flex items-center justify-center gap-2 text-[#384091] hover:bg-[#384091]/5">
                  <ImageIcon className="h-4 w-4" /><span className="text-sm font-medium">Cargar imagen</span>
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        {!readOnly && (
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" className="flex-1" onClick={downloadPdf} disabled={saving || sending}>
              <Download className="h-4 w-4 mr-1" /> Descargar PDF
            </Button>
            <Button className="flex-1 bg-[#384091] hover:bg-[#2D3375] text-white h-11"
              onClick={send} disabled={sending || saving}>
              {sending ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Send className="h-5 w-5 mr-2" />}
              {estado === "completado" ? "Regenerar y Reenviar" : "Completar y Enviar"}
            </Button>
          </div>
        )}
        {readOnly && (
          <Button variant="outline" className="w-full" onClick={downloadPdf}>
            <Download className="h-4 w-4 mr-1" /> Descargar PDF
          </Button>
        )}
      </main>
    </div>
  );
}
